// src/data/editions/EditionContext.jsx
//
// Provides the active edition to the enterprise console subtree.
//
// Wiring (parent — e.g. ExecutivePortal / EnterpriseConsole):
//   import { EditionProvider } from '../../data/editions/EditionContext';
//   // drive it off the org doc in production:
//   <EditionProvider org={organization}> ...console... </EditionProvider>
//   // or pin it for a demo / preview:
//   <EditionProvider editionId="aviation"> ...console... </EditionProvider>
//
// In a component:
//   import { useEdition } from '../../data/editions/EditionContext';
//   const edition = useEdition();
//   edition.readiness.groups, edition.palette.deep, ...
//
// If the console is rendered WITHOUT a provider, the context falls back to the
// default edition, so nothing crashes mid-migration.

import React, { createContext, useContext, useMemo } from 'react';
import { getEdition, resolveEditionId, DEFAULT_EDITION } from './index';

const EditionContext = createContext(getEdition(DEFAULT_EDITION));

export function EditionProvider({ org, editionId, children }) {
    // An explicit editionId (demo / preview) wins; otherwise resolve from the org.
    const edition = useMemo(
        () => getEdition(editionId || resolveEditionId(org)),
        [editionId, org]
    );
    return (
        <EditionContext.Provider value={edition}>
            {children}
        </EditionContext.Provider>
    );
}

export function useEdition() {
    return useContext(EditionContext);
}

// Convenience: spreads the edition palette onto an element as CSS variables, so
// Tailwind arbitrary classes like `bg-[var(--brand-deep)]` resolve at runtime
// and the colors switch with the edition. Usage:
//   <div style={editionVars(edition)}> ... </div>
export function editionVars(edition) {
    const p = (edition && edition.palette) || {};
    return {
        '--brand-deep': p.deep,
        '--brand-blue': p.blue,
        '--brand-yellow': p.yellow
    };
}
